import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowUpRight } from 'lucide-react';
import { ui } from '../../../styles/variables';
import logo from '../../../assets/logo-compressed.svg';

// The logged-in YourEDU app.
const MAIN_APP_URL = 'https://app.youredu.com';

const Bar = styled.header`
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(255, 255, 255, 0.86);
  backdrop-filter: saturate(180%) blur(10px);
  border-bottom: 1px solid ${ui.line};
`;

const Inner = styled.div`
  max-width: ${ui.maxWidth};
  margin: 0 auto;
  height: ${ui.headerHeight};
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 0 28px;

  @media (max-width: 600px) {
    gap: 10px;
    padding: 0 16px;
  }
`;

const Brand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 9px;
  text-decoration: none;
`;

const BrandLogo = styled.img`
  height: 28px;
  width: auto;
  display: block;
`;

const BrandBadge = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${ui.brand};
  background: ${ui.brandTint};
  padding: 3px 8px;
  border-radius: 999px;

  @media (max-width: 600px) {
    display: none;
  }
`;

const Nav = styled.nav`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;

  @media (max-width: 600px) {
    gap: 2px;
  }
`;

const NavItem = styled(NavLink)`
  font-size: 14.5px;
  font-weight: 500;
  color: ${ui.muted};
  padding: 8px 12px;
  border-radius: 9px;
  text-decoration: none;
  white-space: nowrap;
  transition: color 0.15s, background 0.15s;

  &:hover {
    color: ${ui.ink};
    background: ${ui.soft};
  }

  &.active {
    color: ${ui.brand};
  }

  @media (max-width: 600px) {
    font-size: 13.5px;
    padding: 8px 7px;
  }
`;

const AppLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14.5px;
  font-weight: 500;
  color: ${ui.ink};
  padding: 8px 14px;
  border: 1px solid ${ui.line};
  border-radius: 9px;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.15s;

  &:hover {
    background: ${ui.soft};
  }

  @media (max-width: 600px) {
    font-size: 13.5px;
    padding: 8px 10px;
    gap: 4px;
  }
`;

// "Back to app" on desktop, compact "App" on mobile so the nav fits narrow widths.
const LabelFull = styled.span`
  @media (max-width: 600px) {
    display: none;
  }
`;

const LabelShort = styled.span`
  display: none;
  @media (max-width: 600px) {
    display: inline;
  }
`;

export const Header: React.FC = () => (
  <Bar>
    <Inner>
      <Brand to="/" aria-label="YourEDU support home">
        <BrandLogo src={logo} alt="YourEDU" />
        <BrandBadge>Support</BrandBadge>
      </Brand>
      <Nav>
        <NavItem to="/" end>
          Help Center
        </NavItem>
        <NavItem to="/contact">Contact</NavItem>
        <AppLink href={MAIN_APP_URL} aria-label="Back to the YourEDU app">
          <LabelFull>Back to App</LabelFull>
          <LabelShort>App</LabelShort>
          <ArrowUpRight size={15} />
        </AppLink>
      </Nav>
    </Inner>
  </Bar>
);
